import React, { useContext, useMemo } from "react";
import { PROFESSIONAL_QUOTES, QUOTE_THEMES } from '../constants/quotes';
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../auth/AuthContext";
import { departmentUsesBugTracker } from "../constants/departments";
import { MARKETING_PORTFOLIO_ROLES, MARKETING_EXECUTION_ROLES } from "../constants/marketingAccess";
import { canManageUsers } from "../constants/userManagement";
import { useDashboardStats, useDashboardCharts, useWeeklyProgress } from "../api/dashboardApi";
import KPICard from "../components/KPICard";
import EmployeeDashboard from "../components/EmployeeDashboard";
import { FiBriefcase, FiAlertCircle, FiCheckCircle, FiPlusCircle, FiFolder, FiUsers, FiZap, FiTrendingUp, FiClock } from 'react-icons/fi';
import { COLORS as C, RADIUS } from "../constants/ui";
import {
  PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, BarChart, Bar, ResponsiveContainer, 
  Legend, AreaChart, Area 
} from "recharts";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', C.done];

/** Empty state component for charts */
const EmptyChartState = ({ icon: Icon = FiFolder, message = "No data available", subMessage }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: 280,
    color: C.inkFaint,
    textAlign: 'center',
    padding: '2rem'
  }}>
    <Icon size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
    <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: C.inkSoft }}>{message}</p>
    {subMessage && <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: C.inkFaint }}>{subMessage}</p>}
  </div>
);

/** Truncate labels (longer limit for horizontal Y-axis) */
const shortName = (v, max = 28) => {
  if (v == null || typeof v !== "string") return "";
  const t = v.trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
};

const fmtMoney = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return String(n ?? "");
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(x);
};

/** Same deep-link pattern as Projects overview → Open sprint board. */
const sprintBoardPath = (projectId, activeSprintId) => {
  const pid = encodeURIComponent(String(projectId));
  if (activeSprintId != null && activeSprintId !== '') {
    return `/sprint-board?projectId=${pid}&sprintId=${encodeURIComponent(String(activeSprintId))}`;
  }
  return `/sprint-board?projectId=${pid}`;
};

/** Compact axis ticks for large budget numbers */
const fmtBudgetAxis = (v) => {
  const x = Number(v);
  if (!Number.isFinite(x)) return "";
  if (Math.abs(x) >= 1e6) return `${(x / 1e6).toFixed(1)}M`;
  if (Math.abs(x) >= 1e3) return `${Math.round(x / 1e3)}k`;
  return String(Math.round(x));
};

/** Row-based height for horizontal bar lists */
const hBarListHeight = (rowCount, { min = 320, max = 560, perRow = 54, header = 64 } = {}) =>
  Math.min(max, Math.max(min, header + Math.max(rowCount, 1) * perRow));

/**
 * Margins are proportional to CHART_H. They were tuned when charts were 380px
 * tall; at 260 a bottom of 88 was a third of the plot, which is what pushed the
 * axis labels into the data.
 */
const M = {
  barProjectX: { top: 16, right: 16, left: 4, bottom: 64 },
  area: { top: 8, right: 12, left: 0, bottom: 8 },
  vBar: { top: 8, right: 24, left: 4, bottom: 8 },
  budgetHBar: { top: 28, right: 20, left: 4, bottom: 8 },
  pie: { top: 4, right: 8, bottom: 8, left: 8 },
  line: { top: 12, right: 24, left: 4, bottom: 24 },
  lineSimple: { top: 24, right: 16, left: 4, bottom: 16 },
};

// 260 rather than 380: with eleven charts on the page that alone was ~1,300px
// of scroll, and none of these plots need the extra height to be readable.
const CHART_H = 260;

/** Finance + org-wide portfolio (aligned with API strip for developer/tester) */
const CONFIDENTIAL_DASHBOARD_ROLES = [
  "superadmin", "admin", "manager",
  ...MARKETING_PORTFOLIO_ROLES,
  "analyst", "product", "product_manager", "lead",
];


const Dashboard = () => {
  const { role, user } = useContext(AuthContext);           
  const currentRole = (role || '').toLowerCase();
  const canViewConfidentialDashboard = CONFIDENTIAL_DASHBOARD_ROLES.includes(currentRole);
  const canInviteTeamMembers = canManageUsers(currentRole);
  const isContributor = ["developer", "tester", ...MARKETING_EXECUTION_ROLES].includes(currentRole);

  const isEmployee = ["developer", "tester", "designer", "devops", "analyst", ...MARKETING_EXECUTION_ROLES].includes(currentRole);

  // IMPORTANT: Call all hooks unconditionally before any early returns
  const { data: stats, isLoading: statsLoading, isFetching: statsFetching, error: statsError, refetch: refetchStats } = useDashboardStats();
  const { data: charts, isLoading: chartsLoading, isFetching: chartsFetching, error: chartsError, refetch: refetchCharts } = useDashboardCharts();
  const { data: weeklyData, isLoading: weeklyLoading, refetch: refetchWeekly } = useWeeklyProgress();

  const budgetData = charts?.budgetData ?? [];
  const budgetVsAllocation = charts?.budgetVsAllocation ?? [];
  /* Memoised because `?? []` builds a fresh array whenever the field is absent,
     which would change the memo dependency below on every render. */
  const issuesPerProject = useMemo(
    () => charts?.issuesPerProject ?? [],
    [charts?.issuesPerProject]
  );
  /* Only the busiest projects. The full list runs to every project that ever
     existed, and the tail is all zero-length bars — rows that cost vertical
     space and say nothing. The query already orders by count DESC, so the
     first ten are genuinely the top ten. */
  const TOP_PROJECTS = 10;
  const topIssuesPerProject = useMemo(
    () => issuesPerProject.slice(0, TOP_PROJECTS),
    [issuesPerProject]
  );
  const surplusHeight = useMemo(
    () => hBarListHeight(budgetData.length, { min: 320, max: 540, perRow: 52, header: 68 }),
    [budgetData.length]
  );
  const budgetVsAllocationHeight = useMemo(
    () => hBarListHeight(budgetVsAllocation.length, { min: 280, max: 540, perRow: 56, header: 76 }),
    [budgetVsAllocation.length]
  );
  const issuesByProjectHeight = useMemo(
    /* max fits TOP_PROJECTS rows at full perRow spacing (68 + 10*54). The old
       580 was a ceiling for an unbounded list and would have squeezed the ten
       bars that are now the whole chart. */
    () => hBarListHeight(topIssuesPerProject.length, { min: 340, max: 608, perRow: 54, header: 68 }),
    [topIssuesPerProject.length]
  );

  const navigate = useNavigate();
  
  // If employee role, render the enhanced EmployeeDashboard (after all hooks are called)
  if (isEmployee) {
    return <EmployeeDashboard />;
  }

  // Initial load — only block when we have NO cached data yet.
  if ((statsLoading && !stats) || (chartsLoading && !charts)) {
    return <div style={msgStyle}>Loading dashboard...</div>;
  }

  // Hard error wall only when we have no cached data at all to render.
  if ((!stats || !charts) && (statsError || chartsError)) {
    const isRetrying = statsFetching || chartsFetching;
    const handleRetry = () => {
      refetchStats();
      refetchCharts();
      refetchWeekly();
    };
    return (
      <div style={{ padding: "1.25rem 1.5rem", backgroundColor: C.sunken, minHeight: "100vh" }}>
        <div style={{
          maxWidth: 600,
          margin: '4rem auto',
          textAlign: 'center',
          padding: '3rem',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            width: 64,
            height: 64,
            background: C.warnSoft,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem'
          }}>
            <FiAlertCircle size={32} color={C.warn} />
          </div>

          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: C.ink, margin: '0 0 0.75rem' }}>
            Dashboard Temporarily Unavailable
          </h2>
          <p style={{ color: C.inkSoft, fontSize: '1rem', margin: '0 0 2rem', lineHeight: 1.6 }}>
            We're having trouble loading your dashboard data. This could be due to a temporary connection issue.
          </p>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #2C8FC7 0%, #1272B3 100%)',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: isRetrying ? 'wait' : 'pointer',
                fontWeight: 600,
                fontSize: '0.95rem',
                boxShadow: '0 2px 8px rgba(44, 143, 199, 0.3)',
                opacity: isRetrying ? 0.7 : 1,
              }}
            >
              {isRetrying ? 'Retrying…' : 'Try again'}
            </button>
            <button
              onClick={() => navigate('/projects')}
              style={{
                padding: '0.75rem 1.5rem',
                background: C.sunken,
                color: C.inkSoft,
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.95rem'
              }}
            >
              Go to Projects
            </button>
          </div>

          <p style={{ color: C.inkFaint, fontSize: '0.85rem', margin: '2rem 0 0' }}>
            If this issue persists, please contact your system administrator.
          </p>
        </div>
      </div>
    );
  }

  // If we still have no data but no error fired (shouldn't usually happen) keep loading hint.
  if (!stats || !charts) {
    return <div style={msgStyle}>Loading dashboard...</div>;
  }

  // Check if there's any data at all
  const hasProjects = (stats.totalProjects ?? 0) > 0;
  const hasTasks = (stats.tasksOpen ?? stats.totalTasks ?? 0) > 0;
  const hasData = hasProjects || hasTasks;

  // If no data at all, show welcome/getting started state
  if (!hasData && canViewConfidentialDashboard) {
    return (
      <div style={{ padding: "1.25rem 1.5rem", backgroundColor: C.sunken, minHeight: "100vh" }}>
        <div style={{
          maxWidth: 800,
          margin: '4rem auto',
          textAlign: 'center',
          padding: '3rem',
          background: '#fff',
          borderRadius: 20,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)'
        }}>
          <div style={{
            width: 80,
            height: 80,
            background: 'linear-gradient(135deg, #EAF6FD 0%, #a9ddf5 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem'
          }}>
            <FiZap size={36} color={C.accent} />
          </div>
          
          <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: C.accentInk, margin: '0 0 0.75rem' }}>
            Welcome to Team Space!
          </h1>
          <p style={{ color: C.inkSoft, fontSize: '1.05rem', margin: '0 0 2rem', lineHeight: 1.6 }}>
            Your dashboard is ready. Start by creating your first project and inviting team members.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <button
              onClick={() => navigate('/projects')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '1rem 1.5rem',
                background: C.accentInk,
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(18, 114, 179, 0.25)'
              }}
            >
              <FiPlusCircle size={20} />
              Create First Project
            </button>
            
            {canInviteTeamMembers && (
            <button
              onClick={() => navigate('/users-management')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '1rem 1.5rem',
                background: '#fff',
                color: C.accentInk,
                border: '2px solid #a9ddf5',
                borderRadius: 12,
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: 'pointer'
              }}
            >
              <FiUsers size={20} />
              Invite Team Members
            </button>
            )}
          </div>

          <div style={{
            background: C.sunken,
            borderRadius: 12,
            padding: '1.25rem',
            textAlign: 'left'
          }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: C.inkSoft, margin: '0 0 0.75rem' }}>
              Quick Start Guide
            </h3>
            <ul style={{ margin: 0, padding: '0 0 0 1.25rem', color: C.inkSoft, fontSize: '0.85rem', lineHeight: 1.8 }}>
              <li><strong>Create a Project</strong> — Set up your first project with budget and timeline</li>
              <li><strong>Invite Team</strong> — Add developers, testers, and managers to your team</li>
              <li><strong>Plan Sprints</strong> — Create sprints and break down work into tasks</li>
              <li><strong>Track Progress</strong> — Use the Sprint Board to manage work and track bugs</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  const quoteIndex = (dayOfYear + currentRole.length) % PROFESSIONAL_QUOTES.length;
  const themeIndex = (dayOfYear + currentRole.length) % QUOTE_THEMES.length;
  const dailyQuote = PROFESSIONAL_QUOTES[quoteIndex];
  const quoteTheme = QUOTE_THEMES[themeIndex];

  return (
    <div style={{ padding: "1.25rem 1.5rem", backgroundColor: C.sunken, minHeight: "100vh" }}>

      {/* DAILY QUOTE */}
      <div style={{
        background: quoteTheme.bg,
        border: `1px solid ${quoteTheme.border}`,
        borderRadius: 10,
        padding: '0.5rem 0.85rem',
        marginBottom: '1rem',
        color: quoteTheme.text,
        fontSize: '0.8rem',
        fontWeight: 600,
        lineHeight: 1.45,
        fontStyle: 'italic',
      }}>
        "{dailyQuote}"
      </div>

      {/* 1. KPI SECTION */}
      <div style={kpiGridStyle}>
        {typeof stats.totalProjects === "number" && (
          <KPICard title="Total Projects" icon={<FiBriefcase size={32} />} value={stats.totalProjects} linkTo="/projects" />
        )}
        <KPICard
          title="Active Sprint · Open Tasks"
          icon={<FiCheckCircle size={32} />}
          value={stats.tasksOpen ?? stats.totalTasks ?? 0}
          linkTo="/sprint-board"
          detail={
            typeof stats.tasksInProgress === 'number' ? (
              <>
                {/* Completion rate first: "91 open" alone says nothing about
                    whether that is healthy. The raw done count sat next to the
                    open count reading like a comparison, when one is a running
                    total and the other is right now. */}
                {(() => {
                  const open = stats.tasksOpen ?? stats.totalTasks ?? 0;
                  const prog = stats.tasksInProgress ?? 0;
                  const done = stats.tasksDone ?? 0;
                  const total = open + prog + done;
                  return total > 0 ? (
                    <>
                      <strong>{Math.round((done / total) * 100)}%</strong> complete
                      {' · '}
                    </>
                  ) : null;
                })()}
                <strong>{stats.tasksInProgress}</strong> in progress
              </>
            ) : null
          }
        />
        {(canViewConfidentialDashboard || currentRole === "tester") && departmentUsesBugTracker(user?.department) && (
          <KPICard
            title="Active Sprint · Open Bugs"
            icon={<FiAlertCircle size={32} />}
            value={stats.totalBugs ?? 0}
            linkTo="/issues"
          />
        )}
      </div>

      {isContributor && (
        <p style={{ fontSize: "0.88rem", color: C.inkSoft, margin: "0 0 1.25rem", maxWidth: 720 }}>
          Company-wide project counts, budget, portfolio, and per-project breakdowns are available to managers and administrators. Use{" "}
          <strong>My Projects</strong> and <strong>Sprint Board</strong> for your assigned work.
        </p>
      )}

      {/* 2. STAKEHOLDER SECTION - Weekly Progress & Project Health */}
      {canViewConfidentialDashboard && (
        <>
          {(() => {
            const allProjects = (weeklyData && weeklyData.projectHealth) || [];
            // Sort priority:
            //   1. Projects with an Active sprint always come first so stakeholders
            //      see live work, not paused/planning projects.
            //   2. Within each tier, lagging projects (behind > atRisk > onTrack)
            //      surface so risk is the first thing you see.
            //   3. Stable alphabetical tiebreaker.
            const HEALTH_RANK = { behind: 0, atRisk: 1, onTrack: 2 };
            const orderedProjects = [...allProjects].sort((a, b) => {
              const aHasSprint = !!a.activeSprint;
              const bHasSprint = !!b.activeSprint;
              if (aHasSprint !== bHasSprint) return aHasSprint ? -1 : 1;
              const ra = HEALTH_RANK[a.healthStatus] ?? 3;
              const rb = HEALTH_RANK[b.healthStatus] ?? 3;
              if (ra !== rb) return ra - rb;
              return (a.projectName || '').localeCompare(b.projectName || '');
            });
            const activeSprintCount = allProjects.filter((p) => !!p.activeSprint).length;
            const needsAttention = allProjects.filter(
              (p) => p.healthStatus === 'behind' || p.healthStatus === 'atRisk'
            ).length;

            // States the section can be in:
            //   1. Still loading the weekly-progress endpoint
            //   2. Endpoint failed entirely (no data + no longer loading)
            //   3. Endpoint succeeded with zero in-flight projects
            //   4. Endpoint succeeded with project data → render carousel
            const isLoading = !weeklyData && weeklyLoading;
            const isFailed = !weeklyData && !weeklyLoading;
            const isEmpty = !!weeklyData && orderedProjects.length === 0;

            return (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 12,
                  margin: '2rem 0 1rem',
                }}>
                  <h2 style={{ fontSize: '1.05rem', fontWeight: 800, color: C.ink, margin: 0 }}>
                    Weekly Project Progress &amp; Health
                  </h2>
                  {orderedProjects.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span
                        style={{
                          padding: '6px 12px',
                          background: C.accentSoft,
                          color: C.accentInk,
                          border: '1px solid #a9ddf5',
                          borderRadius: 999,
                          fontSize: '0.78rem',
                          fontWeight: 700,
                        }}
                      >
                        {activeSprintCount} active sprint{activeSprintCount === 1 ? '' : 's'} running
                      </span>
                      <span
                        style={{
                          padding: '6px 12px',
                          background: needsAttention > 0 ? C.warnSoft : '#ecfdf5',
                          color: needsAttention > 0 ? '#92400e' : '#065f46',
                          border: `1px solid ${needsAttention > 0 ? '#fde68a' : '#a7f3d0'}`,
                          borderRadius: 999,
                          fontSize: '0.78rem',
                          fontWeight: 700,
                        }}
                      >
                        {needsAttention > 0
                          ? `${needsAttention} of ${orderedProjects.length} need attention`
                          : `All ${orderedProjects.length} projects on track`}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: C.inkFaint }}>
                        ← scroll to see all projects →
                      </span>
                    </div>
                  )}
                </div>

                {isLoading && (
                  <div style={{
                    background: '#fff',
                    border: `1px solid ${C.line}`,
                    borderRadius: 12,
                    padding: '1.25rem 1.5rem',
                    color: C.inkSoft,
                    fontSize: '0.9rem',
                    marginBottom: '2rem',
                  }}>
                    Loading project health…
                  </div>
                )}

                {isFailed && (
                  <div style={{
                    background: '#fff',
                    border: '1px solid #fecaca',
                    borderRadius: 12,
                    padding: '1rem 1.25rem',
                    color: '#991b1b',
                    fontSize: '0.9rem',
                    marginBottom: '2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}>
                    <span>
                      Couldn't load project health right now. Other dashboard data is still available.
                    </span>
                    <button
                      onClick={() => refetchWeekly && refetchWeekly()}
                      style={{
                        padding: '6px 14px',
                        background: C.danger,
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                      }}
                    >
                      Try again
                    </button>
                  </div>
                )}

                {isEmpty && (
                  <div style={{
                    background: '#fff',
                    border: `1px dashed ${C.upcoming}`,
                    borderRadius: 12,
                    padding: '1.25rem 1.5rem',
                    color: C.inkSoft,
                    fontSize: '0.9rem',
                    marginBottom: '2rem',
                  }}>
                    No in-flight projects to display. Create a project or move one out of <strong>Completed / Cancelled / Archived</strong> to see live progress here.
                  </div>
                )}

                {orderedProjects.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      gap: '1rem',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      paddingTop: '0.5rem',
                      paddingBottom: '0.75rem',
                      marginBottom: '2rem',
                      scrollSnapType: 'x mandatory',
                      WebkitOverflowScrolling: 'touch',
                      scrollbarWidth: 'thin',
                    }}
                  >
                    {orderedProjects.map((project) => {
                const healthColors = {
                  onTrack: { bg: C.doneSoft, border: C.done, text: '#065f46', icon: '✓' },
                  atRisk: { bg: C.warnSoft, border: C.warn, text: '#92400e', icon: '⚠' },
                  behind: { bg: C.dangerSoft, border: C.danger, text: '#991b1b', icon: '✕' }
                };
                const health = healthColors[project.healthStatus] || healthColors.onTrack;
                const boardPath = sprintBoardPath(project.projectId, project.activeSprintId);
                
                return (
                  <div
                    key={project.projectId}
                    role="button"
                    tabIndex={0}
                    title={`Open sprint board — ${project.projectName}`}
                    onClick={() => navigate(boardPath)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(boardPath);
                      }
                    }}
                    style={{
                    background: '#fff',
                    border: `2px solid ${health.border}`,
                    borderRadius: 12,
                    padding: '1.25rem',
                    position: 'relative',
                    overflow: 'hidden',
                    // A hard 320px overflows a phone once the page padding is
                    // counted; min() keeps the desktop size and lets it shrink.
                    flex: '0 0 min(320px, 85vw)',
                    minWidth: 'min(320px, 85vw)',
                    maxWidth: 320,
                    scrollSnapAlign: 'start',
                    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.15s ease, transform 0.12s ease',
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 6px 18px rgba(15, 23, 42, 0.12)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(15, 23, 42, 0.06)';
                      e.currentTarget.style.transform = 'none';
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      padding: '4px 12px',
                      background: health.bg,
                      color: health.text,
                      borderRadius: 20,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      whiteSpace: 'nowrap',
                    }}>
                      {health.icon} {project.healthStatus === 'onTrack' ? 'On Track' : project.healthStatus === 'atRisk' ? 'At Risk' : 'Behind'}
                    </div>

                    <h4 style={{
                      fontSize: '1.05rem',
                      fontWeight: 700,
                      color: C.ink,
                      margin: '0 0 0.25rem',
                      paddingRight: 88,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }} title={project.projectName}>
                      {project.projectName}
                    </h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                      {project.projectStatus && (
                        <span style={{
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: C.sunken,
                          color: C.inkSoft,
                          border: `1px solid ${C.line}`,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}>
                          {project.projectStatus}
                        </span>
                      )}
                      {project.activeSprint && (
                        <span style={{ fontSize: '0.75rem', color: C.inkSoft }}>
                          📅 {project.activeSprint}
                        </span>
                      )}
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: C.inkSoft, marginBottom: 4 }}>Completion</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 800, color: C.accent }}>{project.completionRate}%</div>
                      </div>
                      <div>
                        {/* A negative "days left" was rendered raw, so a sprint
                            one day past its end read as "-1". Say it plainly. */}
                        <div style={{ fontSize: '0.7rem', color: C.inkSoft, marginBottom: 3 }}>
                          {project.daysRemaining === null
                            ? 'Status'
                            : project.daysRemaining < 0
                              ? 'Sprint Overran'
                              : 'Sprint Days Left'}
                        </div>
                        <div style={{
                          fontSize: project.daysRemaining !== null ? '1.25rem' : '0.9rem',
                          fontWeight: 800,
                          color: project.daysRemaining !== null && project.daysRemaining < 7 ? C.danger : C.ink,
                        }}>
                          {project.daysRemaining === null
                            ? 'No Active Sprint'
                            : project.daysRemaining < 0
                              ? `${Math.abs(project.daysRemaining)}d over`
                              : project.daysRemaining}
                        </div>
                      </div>
                      <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                        <div
                          style={{ fontSize: '0.75rem', color: C.inkSoft, marginBottom: 6 }}
                          title="Task-type issues in this sprint (or project if no sprint). In Review is counted as done; Blocked is counted as in progress."
                        >
                          Tasks
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'stretch',
                            gap: 8,
                          }}
                        >
                          {[
                            { label: 'Open', value: project.tasksOpen ?? 0, bg: C.sunken, border: C.line, num: C.ink },
                            {
                              label: 'In progress',
                              value: project.tasksInProgress ?? 0,
                              bg: C.accentSoft,
                              border: '#a9ddf5',
                              num: C.accentInk,
                            },
                            { label: 'Done', value: project.tasksDone ?? 0, bg: '#ecfdf5', border: '#a7f3d0', num: '#065f46' },
                          ].map((seg) => (
                            <div
                              key={seg.label}
                              style={{
                                flex: '1 1 auto',
                                minWidth: '5.5rem',
                                padding: '6px 10px',
                                borderRadius: 8,
                                background: seg.bg,
                                border: `1px solid ${seg.border}`,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                              }}
                            >
                              <span style={{ fontSize: '0.65rem', fontWeight: 700, color: C.inkSoft, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                                {seg.label}
                              </span>
                              <span style={{ fontSize: '1.1rem', fontWeight: 800, color: seg.num, lineHeight: 1 }}>{seg.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ marginTop: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: C.inkSoft, marginBottom: 4 }}>
                        <span>{project.activeSprint ? 'Sprint Progress' : 'Overall Progress'}</span>
                        {project.expectedProgress > 0 && <span>Expected: {project.expectedProgress}%</span>}
                      </div>
                      <div style={{
                        width: '100%',
                        height: 8,
                        background: C.line,
                        borderRadius: 4,
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${project.actualProgress}%`,
                          height: '100%',
                          background: health.border,
                          borderRadius: 4,
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </div>
                  </div>
                );
              })}
                  </div>
                )}
              </>
            );
          })()}

          {/* Charts Grid for Stakeholder Metrics — only render when payload arrived */}
          {weeklyData && (
          <div style={mainGridStyle}>
            
            {/* Timeline vs Actual Progress */}
            {weeklyData.timelineComparison && weeklyData.timelineComparison.length > 0 && (
              <div style={chartCardStyle}>
                <h3 style={chartTitleStyle}>Timeline vs Actual Progress</h3>
                <div style={chartInnerStyle}>
                  <ResponsiveContainer width="100%" height={CHART_H}>
                    <BarChart data={weeklyData.timelineComparison} margin={M.vBar}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.sunken} />
                      <XAxis
                        dataKey="projectName"
                        tick={{ fontSize: 10 }}
                        angle={-40}
                        textAnchor="end"
                        height={62}
                        minTickGap={4}
                        tickFormatter={(v) => (String(v).length > 14 ? `${String(v).slice(0, 14)}…` : v)}
                      />
                      <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="expected" fill={C.inkFaint} name="Expected %" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="actual" fill={C.accent} name="Actual %" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Team Velocity Trend */}
            {weeklyData.velocityTrend && weeklyData.velocityTrend.length > 0 && (
              <div style={chartCardStyle}>
                <h3 style={chartTitleStyle}>Team Velocity Trend</h3>
                <div style={chartInnerStyle}>
                  <ResponsiveContainer width="100%" height={CHART_H}>
                    <LineChart data={weeklyData.velocityTrend} margin={M.lineSimple}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.sunken} />
                      <XAxis
                        dataKey="sprint_name"
                        tick={{ fontSize: 10 }}
                        angle={-30}
                        textAnchor="end"
                        height={54}
                        minTickGap={4}
                        tickFormatter={(v) => (String(v).length > 12 ? `${String(v).slice(0, 12)}…` : v)}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="points_completed" stroke={C.done} strokeWidth={3} name="Points Completed" dot={{ r: 5 }} />
                      <Line type="monotone" dataKey="points_planned" stroke={C.inkFaint} strokeWidth={2} strokeDasharray="5 5" name="Points Planned" dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
          )}
        </>
      )}

      {/* 3. MAIN CHARTS GRID */}
      <div style={mainGridStyle}>

        {/* Graph: Issue Burndown/Trend */}
        <div style={chartCardStyle}>
          <h3 style={chartTitleStyle}>Issues Created per Sprint</h3>
          <div style={chartInnerStyle}>
            {(charts.issuesPerSprint ?? []).length > 0 ? (
              <ResponsiveContainer width="100%" height={CHART_H}>
                <AreaChart data={charts.issuesPerSprint} margin={M.area}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.sunken} vertical={false} />
                  {/* No interval={0}: forcing a label per sprint collided them into an
                      unreadable band. Recharts thins them to what fits, and the
                      tooltip still names every point. */}
                  <XAxis
                    dataKey="sprint"
                    tick={{ fontSize: 10 }}
                    minTickGap={12}
                    height={28}
                    tickFormatter={(v) => (String(v).length > 12 ? `${String(v).slice(0, 12)}…` : v)}
                  />
                  <YAxis tick={{ fontSize: 12 }} width={40} domain={[0, "auto"]} allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke={C.done} strokeWidth={2} fill="#86efac" fillOpacity={0.85} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message="No sprint data yet" subMessage="Create sprints and add issues to see trends" />
            )}
          </div>
        </div>

        {/* Graph: Team Load */}
        <div style={chartCardStyle}>
          <h3 style={chartTitleStyle}>Workload by Team</h3>
          <div style={chartInnerStyle}>
            {(charts.teamLoad ?? []).length > 0 ? (
              <ResponsiveContainer width="100%" height={CHART_H}>
                <BarChart
                  data={charts.teamLoad}
                  layout="vertical"
                  margin={M.vBar}
                  barCategoryGap="12%"
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={C.line} />
                  <XAxis type="number" tick={{ fontSize: 12 }} domain={[0, "auto"]} />
                  <YAxis dataKey="assigneeteam" type="category" width={96} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill={C.accent} maxBarSize={44} radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState icon={FiUsers} message="No team workload data" subMessage="Assign issues to team members" />
            )}
          </div>
        </div>

        {/* Graph: Issue Type Distribution */}
        <div style={chartCardStyle}>
          <h3 style={chartTitleStyle}>Bugs vs Tasks Distribution</h3>
          <div style={chartInnerStyle}>
            {(charts.issuesByType ?? []).length > 0 ? (
              <ResponsiveContainer width="100%" height={CHART_H}>
                <PieChart margin={M.pie}>
                  <Pie
                    data={charts.issuesByType}
                    cx="50%"
                    cy="44%"
                    dataKey="count"
                    nameKey="issuetypename"
                    innerRadius={48}
                    outerRadius={78}
                    paddingAngle={2}
                  >
                    {charts.issuesByType.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, name]} />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message="No issues created yet" subMessage="Create tasks and bugs to see distribution" />
            )}
          </div>
        </div>

        {canViewConfidentialDashboard && (
        <div style={chartCardStyle}>
          <h3 style={chartTitleStyle}>Project Portfolio Status</h3>
          <div style={chartInnerStyle}>
            {(charts.statusDistribution ?? []).length > 0 ? (
              <ResponsiveContainer width="100%" height={CHART_H}>
                <PieChart margin={M.pie}>
                  <Pie
                    data={charts.statusDistribution}
                    cx="50%"
                    // Pulled up so the legend below has room; at radius 100 the
                    // wrapped legend was landing on the pie edge.
                    cy="44%"
                    dataKey="count"
                    nameKey="status"
                    outerRadius={74}
                    paddingAngle={2}
                    label={false}
                  >
                    {charts.statusDistribution.map((_, i) => <Cell key={i} fill={COLORS[(i + 2) % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} projects`, name]} />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: 12, paddingTop: 8, lineHeight: 1.4 }}
                    formatter={(value, entry) => {
                      const total = charts.statusDistribution.reduce((s, r) => s + (Number(r.count) || 0), 0);
                      const n = Number(entry?.payload?.count ?? entry?.payload?.value) || 0;
                      const pct = total ? Math.round((n / total) * 100) : 0;
                      return `${value} (${pct}%)`;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState icon={FiBriefcase} message="No projects yet" subMessage="Create your first project to get started" />
            )}
          </div>
        </div>
        )}

        {/* Graph: Resolution Rate */}
        <div style={chartCardStyle}>
          <h3 style={chartTitleStyle}>Resolution Efficiency (Hours)</h3>
          <div style={chartInnerStyle}>
            {(charts.resolutionTime ?? []).length > 0 ? (
              <ResponsiveContainer width="100%" height={CHART_H}>
                <LineChart data={charts.resolutionTime} margin={M.line}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.sunken} />
                  <XAxis
                    dataKey="sprint"
                    tick={{ fontSize: 10 }}
                    minTickGap={12}
                    height={28}
                    padding={{ left: 12, right: 12 }}
                    tickFormatter={(v) => (String(v).length > 12 ? `${String(v).slice(0, 12)}…` : v)}
                  />
                  <YAxis tick={{ fontSize: 12 }} width={52} tickFormatter={(v) => (Number.isFinite(v) ? Math.round(v) : v)} />
                  <Tooltip
                    formatter={(v) =>
                      Number.isFinite(Number(v)) ? [`${Math.round(Number(v))} h`, "Avg resolution"] : [v, ""]
                    }
                  />
                  <Line type="monotone" dataKey="avg_hours" name="Avg hours" stroke="#ea580c" strokeWidth={3} dot={{ r: 5 }} connectNulls isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message="No resolution data" subMessage="Close some issues to track efficiency" />
            )}
          </div>
        </div>

        {canViewConfidentialDashboard && (
        <div style={chartCardStyle}>
          <h3 style={chartTitleStyle}>
            {issuesPerProject.length > TOP_PROJECTS
              ? `Issues per Project — top ${TOP_PROJECTS}`
              : "Issues per Project"}
          </h3>
          <div style={{ ...chartInnerStyle, minHeight: issuesByProjectHeight, height: issuesByProjectHeight }}>
            {topIssuesPerProject.length > 0 ? (
              <ResponsiveContainer width="100%" height={issuesByProjectHeight}>
                <BarChart
                  layout="vertical"
                  data={topIssuesPerProject}
                  margin={M.budgetHBar}
                  barCategoryGap="16%"
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={C.line} />
                  <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} domain={[0, "auto"]} />
                  <YAxis
                    type="category"
                    dataKey="projectname"
                    width={210}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => shortName(v, 30)}
                  />
                  <Tooltip />
                  <Bar dataKey="count" fill={C.warn} name="Issues" maxBarSize={36} radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState icon={FiBriefcase} message="No issues logged" subMessage="Create issues in your projects" />
            )}
          </div>
        </div>
        )}

        {canViewConfidentialDashboard && (
        <>
        <div style={chartCardStyle}>
          <h3 style={chartTitleStyle}>Spend This Month by Project</h3>
          <div style={{ ...chartInnerStyle, minHeight: surplusHeight, height: surplusHeight }}>
            {budgetData.some((d) => Number(d.currentMonth) > 0) ? (
              <ResponsiveContainer width="100%" height={surplusHeight}>
                <BarChart
                  layout="vertical"
                  data={budgetData}
                  margin={M.budgetHBar}
                  barCategoryGap="14%"
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={C.line} />
                  <XAxis type="number" tickFormatter={fmtBudgetAxis} tick={{ fontSize: 12 }} domain={[0, "auto"]} />
                  <YAxis
                    type="category"
                    dataKey="projectname"
                    width={210}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => shortName(v, 30)}
                  />
                  <Tooltip formatter={(value) => [fmtMoney(value), "This month"]} />
                  <Bar dataKey="currentMonth" fill={C.done} name="This month spend" maxBarSize={38} radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message="No spend logged this month" subMessage="Add monthly spends from a project's Budget Preview" />
            )}
          </div>
        </div>

        <div style={{ ...chartCardStyle, gridColumn: "span 1" }}>
          <h3 style={chartTitleStyle}>Monthly Spend by Project (This Year)</h3>
          <div style={chartInnerStyle}>
            {(charts.budgetTrend ?? []).some((r) => Number(r.total) > 0) ? (
              <ResponsiveContainer width="100%" height={CHART_H}>
                <BarChart data={charts.budgetTrend} margin={M.lineSimple}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.sunken} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} interval={0} />
                  <YAxis tick={{ fontSize: 12 }} width={48} tickFormatter={fmtBudgetAxis} />
                  <Tooltip
                    formatter={(value, name) => [fmtMoney(value), name]}
                    cursor={{ fill: "rgba(99, 102, 241, 0.06)" }}
                  />
                  <Legend verticalAlign="top" align="center" wrapperStyle={{ fontSize: 12, top: 0 }} />
                  {(charts.budgetTrendSeries ?? []).map((s) => (
                    <Bar
                      key={s.key}
                      dataKey={s.key}
                      name={shortName(s.label, 22)}
                      stackId="spend"
                      fill={s.color}
                      maxBarSize={42}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message="No spend history yet" subMessage="Add monthly spends from a project's Budget Preview" />
            )}
          </div>
        </div>

        {budgetVsAllocation.length > 0 && (
          <div style={{ ...chartCardStyle, gridColumn: "1 / -1" }}>
            <h3 style={chartTitleStyle}>Yearly Budget vs Spent (YTD)</h3>
            <div style={{ ...chartInnerStyle, minHeight: budgetVsAllocationHeight, height: budgetVsAllocationHeight }}>
              <ResponsiveContainer width="100%" height={budgetVsAllocationHeight}>
                <BarChart
                  layout="vertical"
                  data={budgetVsAllocation}
                  margin={M.budgetHBar}
                  barCategoryGap="14%"
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={C.line} />
                  <XAxis
                    type="number"
                    tickFormatter={fmtBudgetAxis}
                    tick={{ fontSize: 12 }}
                    domain={[0, (max) => (max <= 0 ? 1 : max * 1.06)]}
                  />
                  <YAxis
                    type="category"
                    dataKey="projectname"
                    width={220}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => shortName(v, 32)}
                  />
                  <Tooltip
                    formatter={(value, name, ctx) => {
                      const row = ctx?.payload || {};
                      const fmt = fmtMoney(value);
                      if (name === "Spent (YTD)") {
                        return [`${fmt} (${row.percentUsed ?? 0}% of yearly)`, name];
                      }
                      return [fmt, name];
                    }}
                    labelFormatter={(label, items) => {
                      const row = items?.[0]?.payload || {};
                      const status =
                        row.status === "over"
                          ? "Over budget"
                          : row.status === "at_risk"
                            ? "Pacing ahead"
                            : row.status === "under"
                              ? "Below pace"
                              : "On track";
                      return `${label} — ${status}`;
                    }}
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    wrapperStyle={{ fontSize: 13, paddingBottom: 8 }}
                  />
                  <Bar
                    dataKey="spent"
                    name="Spent (YTD)"
                    stackId="alloc"
                    maxBarSize={36}
                    radius={[6, 0, 0, 6]}
                  >
                    {budgetVsAllocation.map((row) => (
                      <Cell
                        key={`spent-${row.projectname}`}
                        fill={
                          row.status === "over"
                            ? C.danger
                            : row.status === "at_risk"
                              ? C.warn
                              : C.done
                        }
                      />
                    ))}
                  </Bar>
                  <Bar
                    dataKey="remaining"
                    name="Remaining"
                    stackId="alloc"
                    fill={C.line}
                    maxBarSize={36}
                    radius={[0, 6, 6, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        </>
        )}

      </div>
    </div>
  );
};

// --- STYLES ---
const mainGridStyle = {
  display: "grid",
  // min() so the 380px floor can't exceed the viewport on a phone — a plain
  // minmax(min(380px, 100%),…) overflows any screen narrower than that.
  gridTemplateColumns: "repeat(auto-fit, minmax(min(340px, 100%), 1fr))",
  gap: "0.85rem",
};

const kpiGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))",
  gap: "0.75rem",
  marginBottom: "1.25rem",
};

const cardStyle = {
  background: "#fff",
  padding: "1.2rem",
  borderRadius: "10px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  border: `1px solid ${C.line}`
};

/** Extra room + no clipping for Recharts SVGs (labels, legends) */
const chartCardStyle = {
  ...cardStyle,
  padding: "0.75rem 0.85rem 0.9rem",
  overflow: "visible",
  minWidth: 0,
};

const chartTitleStyle = {
  margin: "0 0 0.4rem 0",
  fontSize: "0.82rem",
  fontWeight: 700,
  color: C.ink,
};

const chartInnerStyle = {
  width: "100%",
  minHeight: CHART_H,
  overflow: "visible",
};

const msgStyle = {
  padding: '3rem', 
  textAlign: 'center', 
  fontSize: '1.2rem', 
  fontFamily: 'sans-serif'
};

export default Dashboard;